import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, X, User, Mail, ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const centerLabels = {
  diplomante: 'Formation Diplomante',
  qualifiante: 'Formation Qualifiante',
  certifiante: 'Formation Certifiante',
  linguistique: 'Formation Linguistique',
};

export default function CallbackWidget({ center, variant = 'floating' }) {
  const [isOpen, setIsOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    center: center || '',
    preferred_time: 'matin',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/callbacks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('Erreur serveur');
      setSubmitted(true);
      toast.success('Demande envoyee ! Nous vous rappellerons bientot.');
    } catch {
      toast.error('Erreur lors de l\'envoi. Veuillez reessayer.');
    } finally {
      setLoading(false);
    }
  };

  if (variant === 'inline') {
    return (
      <div className="bg-gradient-to-r from-[#003366] to-[#001a33] rounded-2xl p-8 md:p-10" data-testid="callback-inline">
        <div className="grid md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-[#FFD700] rounded-full flex items-center justify-center">
                <Phone className="text-[#003366]" size={24} />
              </div>
              <h3 className="font-heading text-2xl font-bold text-white">
                Nous vous rappelons
              </h3>
            </div>
            <p className="text-white/70 leading-relaxed">
              Laissez vos coordonnees et un conseiller CEFAT vous contactera pour repondre a toutes vos questions sur nos formations. Vous pouvez aussi nous contacter directement par{' '}
              <a href="https://wa.me/2250712651719" target="_blank" rel="noopener noreferrer" className="text-[#25D366] font-semibold hover:underline">WhatsApp</a>.
            </p>
          </div>

          <AnimatePresence mode="wait">
            {submitted ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-6"
              >
                <CheckCircle className="text-[#FFD700] mx-auto mb-4" size={48} />
                <p className="text-white font-semibold text-lg">Merci !</p>
                <p className="text-white/70">Un conseiller vous rappellera tres bientot.</p>
              </motion.div>
            ) : (
              <motion.form
                key="form"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                onSubmit={handleSubmit}
                className="space-y-4"
                data-testid="callback-inline-form"
              >
                <div>
                  <input
                    type="text"
                    placeholder="Votre nom complet *"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:border-[#FFD700] transition-colors"
                    data-testid="callback-name-input"
                    required
                  />
                </div>
                <div>
                  <input
                    type="tel"
                    placeholder="Votre numero de telephone *"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:border-[#FFD700] transition-colors"
                    data-testid="callback-phone-input"
                    required
                  />
                </div>
                <div>
                  <input
                    type="email"
                    placeholder="Votre email *"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:border-[#FFD700] transition-colors"
                    data-testid="callback-email-input"
                    required
                  />
                </div>
                {!center && (
                  <select
                    value={form.center}
                    onChange={(e) => setForm({ ...form, center: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white focus:outline-none focus:border-[#FFD700] transition-colors"
                    data-testid="callback-center-select"
                  >
                    <option value="" className="text-gray-900">Choisir un centre</option>
                    {Object.entries(centerLabels).map(([key, label]) => (
                      <option key={key} value={key} className="text-gray-900">{label}</option>
                    ))}
                  </select>
                )}
                <select
                  value={form.preferred_time}
                  onChange={(e) => setForm({ ...form, preferred_time: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white focus:outline-none focus:border-[#FFD700] transition-colors"
                  data-testid="callback-time-select"
                >
                  <option value="matin" className="text-gray-900">Matin (8h-12h)</option>
                  <option value="apres-midi" className="text-gray-900">Apres-midi (12h-17h)</option>
                  <option value="soir" className="text-gray-900">Soir (17h-19h)</option>
                </select>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#FFD700] text-[#003366] hover:bg-[#e6c200] rounded-full py-3 font-bold text-lg transition-all hover:scale-[1.02]"
                  data-testid="callback-submit-btn"
                >
                  {loading ? 'Envoi...' : 'Demander un rappel'}
                  {!loading && <ArrowRight className="ml-2" size={18} />}
                </Button>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </div>
    );
  }

  // Floating variant (FAB button)
  return (
    <>
      <motion.button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-40 bg-[#DC143C] text-white w-14 h-14 rounded-full shadow-xl flex items-center justify-center hover:bg-[#B01030] transition-all hover:scale-110"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        data-testid="callback-fab-btn"
        aria-label="Demander un rappel telephonique"
      >
        <Phone size={24} />
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-24 right-6 z-50 w-80 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden"
            data-testid="callback-floating-panel"
          >
            <div className="bg-[#003366] p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Phone className="text-[#FFD700]" size={20} />
                <h4 className="text-white font-bold">Nous vous rappelons</h4>
              </div>
              <button onClick={() => setIsOpen(false)} className="text-white/70 hover:text-white" data-testid="callback-close-btn">
                <X size={20} />
              </button>
            </div>

            <AnimatePresence mode="wait">
              {submitted ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="p-6 text-center"
                >
                  <CheckCircle className="text-green-500 mx-auto mb-3" size={40} />
                  <p className="font-semibold text-[#003366]">Merci !</p>
                  <p className="text-gray-500 text-sm mt-1">Un conseiller vous rappellera bientot.</p>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  onSubmit={handleSubmit}
                  className="p-4 space-y-3"
                  data-testid="callback-floating-form"
                >
                  <div className="relative">
                    <User className="absolute left-3 top-3 text-gray-400" size={16} />
                    <input
                      type="text"
                      placeholder="Votre nom *"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 focus:outline-none focus:border-[#003366] text-sm"
                      data-testid="callback-float-name"
                      required
                    />
                  </div>
                  <div className="relative">
                    <Phone className="absolute left-3 top-3 text-gray-400" size={16} />
                    <input
                      type="tel"
                      placeholder="Telephone *"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 focus:outline-none focus:border-[#003366] text-sm"
                      data-testid="callback-float-phone"
                      required
                    />
                  </div>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 text-gray-400" size={16} />
                    <input
                      type="email"
                      placeholder="Email *"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 focus:outline-none focus:border-[#003366] text-sm"
                      data-testid="callback-float-email"
                      required
                    />
                  </div>
                  {!center && (
                    <select
                      value={form.center}
                      onChange={(e) => setForm({ ...form, center: e.target.value })}
                      className="w-full px-3 py-2.5 rounded-lg border border-gray-200 focus:outline-none focus:border-[#003366] text-sm text-gray-700"
                      data-testid="callback-float-center"
                    >
                      <option value="">Centre (optionnel)</option>
                      {Object.entries(centerLabels).map(([key, label]) => (
                        <option key={key} value={key}>{label}</option>
                      ))}
                    </select>
                  )}
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#DC143C] hover:bg-[#B01030] text-white rounded-full py-2.5 font-semibold text-sm"
                    data-testid="callback-float-submit"
                  >
                    {loading ? 'Envoi...' : 'Me rappeler'}
                  </Button>
                </motion.form>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
